// Step 9 — AI-onboarding (T21). Twee-koloms layout:
//   - Links: AI-conversation (zal Decca-fullscreen worden in echte impl)
//   - Rechts: live Theory of Change (effects/goals/activities die mee-evolueren)
//
// Drie UX-modi (zie spec docs/features/onboarding-flow.md "AI TOC-strategie"):
//   1. Partner-TOC seed → AI herschrijft impactdoel + curated lists
//   2. Sector-netwerk → AI start met sector-filter, geen impactdoel-template
//   3. Cold start → AI doet sector-suggestie op basis van Step 2 org-info
//
// Voor MVP/design-preview: mock conversation + mock TOC. Echte integratie met
// Decca + OpenAI + T20 generateInitialTOC volgt later.

import { useContext, useEffect, useMemo, useState } from 'react'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import { db } from '../../../firebase/config'
import useDevPreview from '../../../hooks/useDevPreview'
import useCompagnyMetaById from '../../../hooks/useCompagnyMetaById'
import { getSectorInfo } from '../../../helpers/meetstandaard/sectorCatalog'

// Mock-content voor dev-preview. Wordt vervangen door T20 generateInitialTOC
// + live OpenAI-stream in productie.
const buildMockConversation = ({ communityName, partnerName, sectorLabels }) => {

    const messages = []

    if (partnerName) {
        messages.push({
            role: 'ai',
            text: `Hoi! Ik ga je helpen met je Theory of Change. Ik heb de TOC van ${partnerName} als startpunt — die passen we samen aan voor ${communityName || 'jouw organisatie'}.`,
        })
    } else if (sectorLabels.length > 0) {
        messages.push({
            role: 'ai',
            text: `Hoi! Op basis van je keuze (${sectorLabels.join(' + ')}) ga ik je helpen je Theory of Change op te zetten. Vertel me eerst wat over je organisatie.`,
        })
    } else {
        messages.push({
            role: 'ai',
            text: `Hoi! We gaan samen je Theory of Change uitwerken. Vertel me eens — wat doet ${communityName || 'jouw organisatie'} en voor wie?`,
        })
    }

    messages.push({
        role: 'user',
        text: 'We helpen jongeren tussen 16-25 met afstand tot de arbeidsmarkt richting werk of opleiding.',
    })

    messages.push({
        role: 'ai',
        text: 'Helder. Op basis daarvan stel ik een impactdoel + 3 effecten voor. Bekijk ze rechts en pas aan waar nodig — je kunt elementen aan- en uitvinken.',
    })

    return messages

}

const buildMockTOC = ({ communityName }) => ({
    impactdoel: `${communityName || 'Jongeren'} hebben duurzaam perspectief op werk of opleiding.`,
    effects: [
        { name: 'Verbeterde zelfredzaamheid', source: 'MSI-standaard' },
        { name: 'Toegenomen werknemersvaardigheden', source: 'MSI-standaard' },
        { name: 'Gegroeid sociaal netwerk', source: 'MSI-standaard' },
    ],
    activities: [
        '1-op-1 coaching trajecten (12 weken)',
        'Werkervaringsplekken bij netwerkpartners',
        'Vaardighedentrainingen (CV, sollicitatie)',
    ],
})

const Step9AITOC = ({ onNext, onPrev }) => {

    const { compagnyId, formState, selectedSectors, partner } = useContext(OnboardingFlowContext)
    const { active: devPreview } = useDevPreview()

    // Resolve context voor mock-conversation. Partner komt uit context — de
    // orchestrator subscribet één keer naar Partners en deelt het record.
    const { compagnyMeta: partnerTenant } = useCompagnyMetaById(partner?.networkCompagnyID)

    const communityName = formState.org?.communityName || ''
    const partnerName = partnerTenant?.CommunityName || partner?.name || null
    const sectorLabels = (selectedSectors || []).map((slug) => getSectorInfo(slug).label)

    const [draftMessage, setDraftMessage] = useState('')
    const [advancing, setAdvancing] = useState(false)
    const [error, setError] = useState('')

    const messages = useMemo(
        () => buildMockConversation({ communityName, partnerName, sectorLabels }),
        [communityName, partnerName, sectorLabels]
    )
    const toc = useMemo(
        () => buildMockTOC({ communityName }),
        [communityName]
    )

    useEffect(() => {
        // TODO: in echte impl: setDeccaState('fullscreen') bij mount,
        //       setDeccaState('closed') bij unmount.
    }, [])

    const next = async () => {
        if (devPreview) {
            onNext()
            return
        }

        if (!compagnyId) {
            onNext()
            return
        }

        setAdvancing(true)
        try {
            const metaSnap = await db.collection('CompagnyMeta')
                .where('CompagnyID', '==', compagnyId)
                .limit(1)
                .get()

            if (!metaSnap.empty) {
                await metaSnap.docs[0].ref.update({
                    OnboardingStatus: { lastStep: 10, completed: false },
                })
            }
            onNext()
        } catch (err) {
            console.error('Step9AITOC next error', err)
            setError(err.message || 'Er ging iets mis. Probeer het opnieuw.')
            setAdvancing(false)
        }
    }

    return (
        <div className="onboarding-step-aitoc">
            <div className="onboarding-aitoc-grid">

                {/* LEFT — AI conversation */}
                <div className="onboarding-aitoc-conversation">
                    <div className="onboarding-aitoc-header">
                        <AutoAwesomeOutlinedIcon className="onboarding-aitoc-header-icon" />
                        <div>
                            <h1>Bouw je Theory of Change</h1>
                            <p>Beantwoord de vragen — rechts groeit je TOC live mee.</p>
                        </div>
                    </div>

                    <div className="onboarding-aitoc-messages">
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={`onboarding-aitoc-message onboarding-aitoc-message--${msg.role}`}
                            >
                                {msg.text}
                            </div>
                        ))}
                    </div>

                    <div className="onboarding-aitoc-composer">
                        <input
                            type="text"
                            value={draftMessage}
                            onChange={(e) => setDraftMessage(e.target.value)}
                            placeholder="Typ je antwoord..."
                            disabled={devPreview}
                        />
                        <button
                            type="button"
                            className="onboarding-aitoc-send"
                            disabled={true}
                            aria-label="Versturen"
                        >
                            <SendOutlinedIcon />
                        </button>
                    </div>

                    {devPreview && (
                        <p className="onboarding-aitoc-dev-note">
                            Dev-preview: bovenstaande gesprek is mock-data. In productie
                            stream't OpenAI hier live mee.
                        </p>
                    )}
                </div>

                {/* RIGHT — live Theory of Change */}
                <div className="onboarding-aitoc-toc">
                    <h2>Jouw Theory of Change</h2>

                    <section className="onboarding-toc-section">
                        <h3>Impactdoel</h3>
                        <p>{toc.impactdoel}</p>
                    </section>

                    <section className="onboarding-toc-section">
                        <h3>Effecten</h3>
                        <ul>
                            {toc.effects.map((eff, idx) => (
                                <li key={idx}>
                                    <strong>{eff.name}</strong>
                                    {eff.source && (
                                        <span className="onboarding-toc-source">— {eff.source}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="onboarding-toc-section">
                        <h3>Activiteiten</h3>
                        <ul>
                            {toc.activities.map((act, idx) => (
                                <li key={idx}>{act}</li>
                            ))}
                        </ul>
                    </section>
                </div>

            </div>

            {error && <p className="onboarding-error">{error}</p>}

            <div className="onboarding-step-actions">
                <button
                    type="button"
                    className="onboarding-button-secondary"
                    onClick={onPrev}
                    disabled={advancing}
                >
                    Terug
                </button>
                <button type="button" onClick={next} disabled={advancing}>
                    {advancing ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Volgende'}
                </button>
            </div>
        </div>
    )

}

export default Step9AITOC
